// Profile manager — owns the player's identity (cat, kingdom, title, education).
// Also owns derived bonuses: certs + degrees + majors stack into four totals
// that other subsystems read via getBonuses().
//
// Defection: change homeKingdom for a wealth cost. The current kingdom slice's
// state moves with the player to the new slot; the old slot resets.

import { Events, emit, on } from '../ui/eventBus.js'
import {
  TitleConfig,
  LicenseConfig,
  DegreeSubjects,
  DegreeTiers,
  DegreeTierOrder,
  DefectionConfig,
  KingdomOrder,
} from '../config/balance.js'
import { state, getProfile, persist } from './state.js'
import * as season from './seasonManager.js'
import { resetKingdom } from './save.js'

export function init() {
  on(Events.DefectRequested, ({ kingdom }) => defect(kingdom))
}

export function isOnboarded() {
  return !!getProfile().onboarded
}

// Onboarding writes the chosen identity in one shot and aligns activeKingdom
// to homeKingdom so the engine immediately starts playing in the right slot.
// classId is derived from the title's class membership when omitted.
export function completeOnboarding({ catName, catColorId, homeKingdom, title, classId, gender = null }) {
  const p = getProfile()
  p.onboarded = true
  p.catName = catName
  p.catColorId = catColorId
  if (gender === 'MALE' || gender === 'FEMALE') p.gender = gender
  p.homeKingdom = homeKingdom
  p.title = title
  p.originalTitle = title // remember the Common-class anchor for resign demotion
  p.classId = classId ?? TitleConfig[title]?.classId ?? null
  // Stamp citizenship start the first time onboarding completes — surfaced
  // in the Registry as "Citizen of {Kingdom} for X days". Don't overwrite
  // if a returning player re-runs onboarding (preserve original tenure).
  if (!p.joinedAt) p.joinedAt = Date.now()
  state.activeKingdom = homeKingdom
  persist()
  emit(Events.OnboardingComplete, { profile: { ...p } })
  emit(Events.ProfileChange, { profile: { ...p } })
}

// Election victory: lift player from Common-class title to an elected title.
export function elevateTitle(titleId) {
  const p = getProfile()
  const cfg = TitleConfig[titleId]
  if (!cfg) return false
  if (!p.originalTitle) p.originalTitle = p.title // belt-and-braces
  p.title = titleId
  p.classId = cfg.classId
  persist()
  emit(Events.ProfileChange, { profile: { ...p } })
  return true
}

// Resignation: drop back to originally-onboarded Common title.
export function demoteFromElected() {
  const p = getProfile()
  const fallback = p.originalTitle
  if (!fallback) return false
  p.title = fallback
  p.classId = TitleConfig[fallback]?.classId ?? null
  persist()
  emit(Events.ProfileChange, { profile: { ...p } })
  return true
}

export function purchaseLicense(licenseId) {
  const cfg = LicenseConfig[licenseId]
  if (!cfg) return false
  const p = getProfile()
  if (p.licenses?.[licenseId]) return false
  if (!season.spendWealth(cfg.cost)) return false
  if (!p.licenses) p.licenses = {}
  p.licenses[licenseId] = true
  persist()
  emit(Events.LicensePurchased, { licenseId })
  emit(Events.ProfileChange, { profile: { ...p } })
  return true
}

export function hasLicense(licenseId) {
  return !!getProfile().licenses?.[licenseId]
}

// ── CR-6 self-education: 8 subjects × 3 tiers (CERT → MASTERS → PHD) ──
// `profile.degrees` maps subject → highest tier earned, e.g. { MILITARY: 'PHD' }.
// A tier is earned by passing its quiz (UI gate) and is granted here, charging
// the tier cost. Tiers must be earned in order within each subject. Each PhD
// permanently raises the click base; every tier adds Honor (honor.js).

// The tier the player would earn next for a subject, or null if PhD already held.
export function nextTierFor(subject) {
  if (!DegreeSubjects.includes(subject)) return null
  const have = getProfile().degrees?.[subject]
  const idx = have ? DegreeTierOrder.indexOf(have) : -1
  return DegreeTierOrder[idx + 1] ?? null
}

export function getDegreeTier(subject) {
  return getProfile().degrees?.[subject] ?? null
}

// Can the player afford to sit `tier`'s quiz in `subject` right now?
// (sequence + enough coin for the exam fee)
export function canEarnDegree(subject, tier) {
  if (nextTierFor(subject) !== tier) return false
  return season.getWealth() >= (DegreeTiers[tier]?.cost ?? Infinity)
}

// Charge the exam fee for ONE quiz attempt. The tuition is paid per sitting —
// win or lose — so a failed attempt costs coin and a retake must pay again.
// Validates sequence + affordability and spends the cost. Returns true if the
// fee was charged (the attempt may begin), false otherwise.
export function payExamFee(subject, tier) {
  if (!DegreeSubjects.includes(subject)) return false
  if (nextTierFor(subject) !== tier) return false // out of sequence / already held
  const cost = DegreeTiers[tier]?.cost
  if (cost == null) return false
  return season.spendWealth(cost) // emits WealthChange + persists
}

// Record a passed quiz as a granted tier. The exam fee was already charged at
// the start of the attempt by payExamFee — this only records the new highest
// tier (no second charge). Returns true on success.
export function earnDegree(subject, tier) {
  if (!DegreeSubjects.includes(subject)) return false
  if (nextTierFor(subject) !== tier) return false // out of sequence / already held
  const p = getProfile()
  if (!p.degrees) p.degrees = {}
  p.degrees[subject] = tier
  persist()
  emit(Events.DegreePurchased, { subject, tier })
  emit(Events.ProfileChange, { profile: { ...p } })
  return true
}

// Number of subjects mastered to PhD — drives the permanent +1/click bonus.
export function phdSubjectCount() {
  const degrees = getProfile().degrees ?? {}
  return Object.values(degrees).filter((t) => t === 'PHD').length
}

// License + title bonuses — summed into four buckets that seasonManager /
// goonManager / taxManager apply at runtime. (CR-6: degrees no longer feed
// these buckets — they drive PhD click-base + Honor instead.)
export function getBonuses() {
  const p = getProfile()
  const bonuses = { pawYieldBonus: 0, goonRateBonus: 0, taxBonus: 0, culturePoints: 0 }
  for (const licenseId of Object.keys(p.licenses ?? {})) {
    apply(bonuses, LicenseConfig[licenseId])
  }
  return bonuses
}

function apply(bag, cfg) {
  if (!cfg || !cfg.bonusKey) return
  bag[cfg.bonusKey] = (bag[cfg.bonusKey] ?? 0) + cfg.bonusValue
}

export function getTitleMultipliers() {
  const t = getProfile().title
  const cfg = TitleConfig[t]
  return {
    pawYieldMult: cfg?.pawYieldMult ?? 1,
    buildingYieldMult: cfg?.buildingYieldMult ?? 1,
    goonRateMult: cfg?.goonRateMult ?? 1,
    taxMult: cfg?.taxMult ?? 1,
  }
}

export function canDefect() {
  return season.getWealth() >= DefectionConfig.cost
}

function defect(toKingdomId) {
  const p = getProfile()
  if (!p.homeKingdom) return false
  if (toKingdomId === p.homeKingdom) return false
  if (!KingdomOrder.includes(toKingdomId)) return false
  if (!season.spendWealth(DefectionConfig.cost)) return false

  // Move the current kingdom slice's state into the new slot; reset the old.
  const fromId = p.homeKingdom
  const carryOver = { ...state.kingdoms[fromId], id: toKingdomId }
  resetKingdom(state, fromId)
  state.kingdoms[toKingdomId] = carryOver
  state.activeKingdom = toKingdomId
  p.homeKingdom = toKingdomId
  persist()
  emit(Events.KingdomChange, { kingdom: toKingdomId })
  emit(Events.ProfileChange, { profile: { ...p } })
  return true
}
